import React, { useEffect, useState } from 'react';
import { Avatar, Modal, Skeleton } from "antd";

import { connect } from 'react-redux';
import { getAssetImages, getAssetLastImage } from "../../appRedux/actions/Asset";
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import MapView from "./MapView";
import { Link } from "react-router-dom";

// Stable empty array so "no images for this asset" is always the same reference
// and cannot trigger a pointless re-render on every store update.
const EMPTY = [];

function LatestImage(props) {
  const [thumbnail, setThumbnail] = useState(EMPTY)
  const [images, setImages] = useState(EMPTY)
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (!props.assetLastImage[props.id])
      props.getAssetLastImage(props.id);
  }, [props.id, props.getAssetLastImage])

  // These mirror the store, which is keyed by asset id, so they MUST follow
  // props.id. They previously guarded on "only set if empty" and did not depend
  // on props.id, so once an instance had an image it kept it forever: any reused
  // instance whose id changed - a detail panel stepping through records, or a
  // table column re-using a row component after paging or sorting - went on
  // showing the first asset's photo. Same for the enlarged gallery.
  //
  // The identity check keeps this cheap: redux hands back the same array
  // reference for an unchanged id, so instances that are not affected by a store
  // update skip the state write entirely.
  useEffect(() => {
    const next = props.assetLastImage[props.id] || EMPTY
    setThumbnail(prev => (prev === next ? prev : next))
  }, [props.assetLastImage, props.id]);

  useEffect(() => {
    const next = props.assetImages[props.id] || EMPTY
    setImages(prev => (prev === next ? prev : next))
  }, [props.assetImages, props.id]);

  // A different asset's gallery starts at its first image.
  useEffect(() => { setCurrentImageIndex(0) }, [props.id]);

  const handleMouseEnter = () => {
    if (!props.assetImages[props.id] || !images || !images.length)
      props.getAssetImages(props.id);
    setVisible(true);
  };

  const handleMouseLeave = () => {
    setVisible(false);
  };

  const handlePrev = () => {
    setCurrentImageIndex(prevIndex => (prevIndex > 0 ? prevIndex - 1 : 0));
  };

  const handleNext = () => {
    setCurrentImageIndex(prevIndex => (prevIndex < images.length - 1 ? prevIndex + 1 : images.length - 1));
  };

  const handleKeyDown = (event) => {
    if (visible) {
      if (event.key === 'ArrowLeft' && currentImageIndex !== 0) {
        handlePrev();
      } else if (event.key === 'ArrowRight' && currentImageIndex !== images.length - 1) {
        handleNext();
      }
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentImageIndex, visible, images]);

  return (
    <>
      <Avatar shape="square" size={55} icon="file-image" src={thumbnail && thumbnail[0] && thumbnail[0]['url']}
        alt={thumbnail && thumbnail[0] && thumbnail[0]['name']} style={{ cursor: 'pointer' }}
        onClick={thumbnail && thumbnail.length && handleMouseEnter} />
      <Modal visible={visible} onCancel={handleMouseLeave} footer={null} centered
        title={
          <div style={{ height: '5vh', marginBottom: '10px' }}>
            <span>
              <Link target={'_blank'}
                to={(images[currentImageIndex] && images[currentImageIndex]['type'] && images[currentImageIndex]['type'] === 'ast') ? `/asset-stock-taking/view/${images[currentImageIndex] && images[currentImageIndex]['voucherId']}` :
                  (images[currentImageIndex] && images[currentImageIndex]['type'] && images[currentImageIndex]['type'] === 'tag') ? `/asset-tagging/list/${images[currentImageIndex] && images[currentImageIndex]['voucherId']}` :
                    (images[currentImageIndex] && images[currentImageIndex]['type'] === 'acv') ? `/asset-condition-voucher/view/${images[currentImageIndex]['voucherId']}` :
                    `/pre-verification/view/${images[currentImageIndex] && images[currentImageIndex]['voucherId']}`
                }>{images[currentImageIndex] && images[currentImageIndex]['voucher']}</Link>
            </span>
            <span style={{
              marginRight: '10px',
              fontSize: '12px',
              float: 'right',
              display: "flex",
              flexDirection: 'column'
            }}>
              <span>{images[currentImageIndex] && images[currentImageIndex]['time'] && new Date(images[currentImageIndex]['time']).toLocaleString()}</span>
              <span>Location: {images[currentImageIndex] && images[currentImageIndex]['location']}</span>
              <span>Sub Location: {images[currentImageIndex] && images[currentImageIndex]['client']}</span>
            </span>
          </div>
        }
        bodyStyle={{ padding: 0 }}
        width="70%"
      // style={{maxWidth: '800px'}}
      >
        <div style={{
          position: 'relative',
          backgroundColor: 'black',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          // height: '100%',
          // width: '100%'
        }}>
          {currentImageIndex !== 0 && (
            <LeftOutlined
              style={{
                position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'white',
                fontSize: '24px', // Adjust the size of the icon
                boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.5)', // Add a shadow
                cursor: 'pointer',
                zIndex: 1000,
              }}
              onClick={handlePrev}
            />
          )}
          {props.loading ? <Skeleton active /> :
            <img alt="preview" src={images && images[currentImageIndex] && images[currentImageIndex]['url']}
              style={{
                position: 'relative',
                width: 'auto',
                height: 'auto',
                display: 'block',
                maxHeight: '70vh', // Adjust max height as needed
                maxWidth: '90%',
                margin: '0 auto',
                // maxHeight: '80vh', // Adjust height as needed
                // maxWidth: '60vw', // Adjust width as needed
                // objectFit: 'contain',
              }} />
          }
          {currentImageIndex !== images.length - 1 && (
            <RightOutlined
              style={{
                position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: 'white',
                fontSize: '24px', // Adjust the size of the icon
                boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.5)', // Add a shadow
                cursor: 'pointer',
                zIndex: 1000,
              }}
              onClick={handleNext}
            />
          )}
        </div>
      </Modal>
    </>
  );
}

const mapStateToProps = (state) => ({
  assetLastImage: state.asset.assetLastImage,
  assetImages: state.asset.assetImages,
  loading: state.commonData.loading,
});

const mapDispatchToProps = { getAssetLastImage, getAssetImages }

export default connect(mapStateToProps, mapDispatchToProps)(LatestImage);
